/**
 * Lo que se le dice al operador sobre a cuántos de un análisis les llega WhatsApp oficial, ANTES de armar la campaña
 * (`GET /audiencias/:id/alcance`). Lo muestran el reporte y «Por dónde y cuándo» de Reactivación y la Audiencia del
 * asistente oficial, con la misma cuenta que el chequeo (las compuertas de la persona de `decidirElegibilidad`).
 *
 * Por qué existe: el archivo del comercio casi nunca trae quién aceptó recibir mensajes. Reactivación (con la política
 * «declarada») le escribía igual a esa gente, y la campaña oficial recién fallaba al confirmar, con «no hay a quién
 * mandarle», después de elegir la plantilla, mapear las variables y mandar la prueba. Acá se dice cuántos son, por qué
 * no les llega a los demás y qué se puede hacer, en el momento en que todavía se puede hacer algo.
 */

import type { AlcanceAnalisis } from "../types";
import { fechaHora, num, plural } from "../../campana-nueva/utils/format";
import { textoYaRecibio } from "./textos";
import { rutaMensajes } from "../../mensajes/logic/mensajes";

/** Cargar contactos con su consentimiento (la otra forma de tener a quién escribirle), en Público desde el 17/10/2026. */
export const RUTA_CARGA_BASE = "/activa/publico/cargas/nueva";
/** Donde se declara el consentimiento de la base en uso (el Historial de cargas de Público, fase 4). */
export const RUTA_DECLARAR_CONSENTIMIENTO = "/activa/publico?vista=cargas";
/** Donde se incluyen los celulares que vinieron sin +54 (`excluirTelefonosAmbiguos`). */
export const RUTA_CONFIG_ENVIO = "/activa/oficial/configuracion?seccion=envio";
/** Segmentar de nuevo, cuando la base cambió desde que se preparó la campaña. */
export const RUTA_NUEVA_CAMPANA = "/activa/segmentaciones/nueva";

export interface ArregloAlcance {
  texto: string;
  label: string;
  ruta: string;
  /** la segmentación que hay que preparar de nuevo: quien lo muestra puede ofrecer el botón en vez del link */
  preparar?: string;
}

export interface ResumenAlcance {
  /** nadie del análisis puede recibir: con esto, la campaña oficial da «no hay a quién mandarle» seguro */
  ninguno: boolean;
  titulo: string;
  /** qué dice el archivo del consentimiento */
  consentimiento: string;
  /** de los que aceptaron, por qué no les llega a algunos (null si les llega a todos) */
  celular: string | null;
  /** a cuántos se les escribió en los días de la fatiga, que no se suman (null si a nadie) */
  fatiga: string | null;
  /** cuántos de los que podían recibir saca una lista de exclusión o una campaña elegida (null si a nadie) */
  exclusiones: string | null;
  /** lo que todavía se descuenta al confirmar (el número es un máximo) */
  nota: string;
  /** qué puede hacer el operador para que le llegue a más gente */
  arreglos: ArregloAlcance[];
}

const n0 = (x: unknown) => (typeof x === "number" && Number.isFinite(x) && x > 0 ? x : 0);

/**
 * `motor`: la campaña se preparó en Nueva campaña con la base de ventas (fase 4). Ahí no se vuelve a analizar un
 * archivo: si el comercio confirmó que sus clientes aceptaron, se declara en la base y se prepara el envío de nuevo.
 */
export function resumenAlcance(a: AlcanceAnalisis, { motor = false }: { motor?: boolean } = {}): ResumenAlcance {
  const pueden = n0(a.pueden);
  const clientes = n0(a.clientes);
  const si = n0(a.consentimiento?.si);
  const no = n0(a.consentimiento?.no);
  const sinDato = n0(a.consentimiento?.sinDato);
  const cel = a.celularDeLosQueSi || ({} as Partial<AlcanceAnalisis["celularDeLosQueSi"]>);
  const ninguno = pueden === 0;

  const titulo = ninguno
    ? `Por WhatsApp oficial no le podés escribir a nadie de ${motor ? "esta campaña" : "este análisis"}`
    : `Por WhatsApp oficial le podés escribir a hasta ${num(pueden)} de ${num(clientes)} ${plural(clientes, "cliente", "clientes")}`;

  // Lo que dice el archivo. El «sólo a quien aceptó» va cuando es la causa: si nadie dijo que sí, es TODA la explicación.
  const dijeronQueNo = `${num(no)} ${plural(no, "dijo", "dijeron")} que no`;
  const consentimiento = si === 0 && no === 0
    ? "Sólo se le puede escribir a quien aceptó recibir mensajes, y el archivo no dice quién aceptó."
    : si === 0
      ? `Sólo se le puede escribir a quien aceptó recibir mensajes, y nadie dijo que sí: ${dijeronQueNo}${sinDato ? ` y de ${num(sinDato)} el archivo no lo dice` : ""}.`
      : `${num(si)} ${plural(si, "aceptó", "aceptaron")} recibir mensajes${sinDato ? `; de ${num(sinDato)} el archivo no lo dice` : ""}${no ? `${sinDato ? " y" : ";"} ${dijeronQueNo}` : ""}.`;

  // De los que aceptaron, los que no tienen un celular que sirva (suman si - pueden).
  const sinCelular = n0(cel.sin_celular);
  const dudosos = n0(cel.telefono_ambiguo);
  const invalidos = n0(cel.telefono_invalido) + n0(cel.numero_eeuu);
  const falta = sinCelular + dudosos + invalidos;
  const partes = [
    sinCelular ? `${num(sinCelular)} sin celular` : "",
    dudosos ? `${num(dudosos)} con el celular sin +54` : "",
    invalidos ? `${num(invalidos)} con un número que no sirve para WhatsApp` : "",
  ].filter(Boolean);
  const celular = falta
    ? `De los que aceptaron, ${num(falta)} no ${plural(falta, "tiene", "tienen")} un celular que sirva: ${partes.join(", ")}.`
    : null;

  // La fatiga ya está descontada de `pueden` (desde el 08/10): antes no, y el detalle de una campaña recién enviada por
  // las líneas prometía el oficial «a hasta 137» que al confirmar quedaban afuera porque les había llegado esa semana.
  const fatigados = n0(a.fatiga?.excluidos);
  const dias = n0(a.fatiga?.dias);
  // la regla de la marca (29/10/2026): K mensajes cada N días, sumando el oficial y los WhatsApp por QR
  const maximo = Math.max(1, n0(a.fatiga?.maximo) || 1);
  const fatiga = fatigados && dias
    ? `${num(fatigados)} ${textoYaRecibio(fatigados, dias, maximo)}, y no ${plural(fatigados, "se suma", "se suman")}: ${
        maximo === 1 ? "a nadie se le vuelve a escribir antes de que pasen." : `la marca manda como mucho ${num(maximo)} cada ${num(dias)} ${plural(dias, "día", "días")}.`
      }`
    : null;
  // un backend anterior no manda la fatiga: ahí todavía se descontaba al confirmar
  const nota = a.fatiga
    ? "Es un máximo: al confirmar se descuentan además las bajas y los números repetidos."
    : "Es un máximo: al confirmar se descuentan además las bajas, los números repetidos y a quien recibió otra promoción hace poco.";

  const arreglos: ArregloAlcance[] = [];
  const quienes = `${plural(sinDato, "quien", `los ${num(sinDato)} que`)} el archivo no dice si ${plural(sinDato, "aceptó", "aceptaron")}`;
  const prep = motor ? a.preparacion : undefined;
  // Sin el dato hay algo para hacer; quien dijo que no, no se convence desde acá. Si el archivo no trae el dato de
  // nadie, falta la columna; si lo trae de algunos, falta completarlo (pedirle «agregá la columna» a un archivo que ya
  // la tiene confunde). En una campaña de Nueva campaña el sí se fija al preparar: con el consentimiento declarado
  // DESPUÉS, falta prepararla de nuevo, y repetir «declaralo» hacía creer que no se había guardado (Llama Leads, 26/10:
  // «igual me sigue diciendo»).
  if (sinDato > 0 && prep?.declaradoDespues && prep.segmentacionId) {
    const cuandoDeclaro = prep.consentimientoDeclaradoAt ? ` el ${fechaHora(prep.consentimientoDeclaradoAt)}` : "";
    const cuandoPreparo = prep.preparadaAt ? ` (el ${fechaHora(prep.preparadaAt)})` : "";
    arreglos.push({
      texto: `Ya declaraste el consentimiento de la base${cuandoDeclaro}, pero esta campaña se preparó antes${cuandoPreparo}: preparala de nuevo para que ${plural(sinDato, "se sume", "se sumen")} ${quienes}. Los grupos y los mensajes quedan como están.`,
      label: "Preparar de nuevo",
      ruta: rutaMensajes(prep.segmentacionId),
      preparar: prep.segmentacionId,
    });
  } else if (sinDato > 0 && prep?.baseCambio) {
    arreglos.push({
      texto: `Esta campaña se preparó con otra base de ventas: para sumar a ${quienes}, segmentá de nuevo con la base en uso y, si el comercio te lo confirmó, declarale el consentimiento.`,
      label: "Nueva campaña",
      ruta: RUTA_NUEVA_CAMPANA,
    });
  } else if (sinDato > 0 && motor) {
    arreglos.push({
      texto: `Si el comercio te confirmó que sus clientes aceptaron recibir promociones, declaralo en la base de ventas y después prepará esta campaña de nuevo: así se ${plural(sinDato, "suma", "suman")} ${quienes}.`,
      label: "Declarar el consentimiento",
      ruta: RUTA_DECLARAR_CONSENTIMIENTO,
    });
  } else if (sinDato > 0) {
    arreglos.push({
      texto: si === 0 && no === 0
        ? "Agregá al archivo una columna que diga quién aceptó (sí o no) y volvé a analizarlo, o cargá los contactos con su consentimiento."
        : `Para sumar a ${plural(sinDato, "quien", `los ${num(sinDato)} que`)} el archivo no dice si ${plural(sinDato, "aceptó", "aceptaron")}, completá ese dato y volvé a analizarlo, o cargalos con su consentimiento.`,
      label: "Cargar con consentimiento",
      ruta: RUTA_CARGA_BASE,
    });
  }
  // Sólo si la marca los deja afuera y hay alguno que aceptó: incluirlos es lo único que los suma.
  if (a.excluirAmbiguos !== false && dudosos > 0) {
    arreglos.push({
      texto: `Si sabés que los ${num(dudosos)} que vinieron sin +54 son celulares, los podés incluir.`,
      label: "Configuración → Envío",
      ruta: RUTA_CONFIG_ENVIO,
    });
  }
  // Las exclusiones (29/10/2026): las listas que valen para todas las campañas y lo elegido en «Excluir» ya están
  // descontadas de `pueden`, como la fatiga; se dice cuántos sacan.
  const sacan = n0(a.exclusiones?.sacan);
  const exclusiones = !sacan ? null
    : n0(a.exclusiones?.porCampanas)
      ? `${num(sacan)} ${plural(sacan, "está", "están")} en una lista de exclusión o ya ${plural(sacan, "recibió", "recibieron")} una campaña que elegiste excluir: no ${plural(sacan, "se suma", "se suman")}.`
      : `${num(sacan)} ${plural(sacan, "está", "están")} en una lista de exclusión de la marca: no ${plural(sacan, "se suma", "se suman")}.`;
  return { ninguno, titulo, consentimiento, celular, fatiga, exclusiones, nota, arreglos };
}

/**
 * Por qué el mismo archivo le llega a unos por un canal y no por el otro, en una línea y sin jerga. Depende de la
 * política de consentimiento de la marca (`GET /activa/settings` → `politicaOptIn`):
 * - «declarada» (la de casi todas): Reactivación le escribe también a quien no dijo nada, porque la marca declaró
 *   tener su permiso. WhatsApp oficial no: Meta pide que cada persona haya dicho que sí. Es la diferencia que nadie le
 *   explicaba al operador: el archivo «funcionaba» por un canal y por el otro daba 0.
 * - «estricta»: los dos le escriben sólo a quien dijo que sí; el oficial pide además un celular bien cargado.
 * - sin saberla (no se pudo leer): lo de Meta, que vale siempre, y que lo otro depende de la marca.
 */
export function porQueDifieren(politica: "estricta" | "declarada" | null | undefined): string {
  if (politica === "declarada") {
    return "Por tus líneas y por email alcanza con el permiso que declaró tu marca; por WhatsApp oficial, Meta pide que cada persona haya dicho que sí.";
  }
  if (politica === "estricta") {
    return "Por tus líneas y por email también le escribís sólo a quien dijo que sí (así lo eligió tu marca); WhatsApp oficial pide además un celular bien cargado.";
  }
  return "Por WhatsApp oficial, Meta pide que cada persona haya dicho que sí; por tus líneas y por email depende del permiso que haya declarado tu marca.";
}
